import { create } from 'zustand';
import { userManagementService, BlockedUser, BlockStatusResponse, ReportSubmission, BlockUserRequest } from '../services/userManagementService';

interface UserManagementState {
  // Blocked users state
  blockedUsers: BlockedUser[];
  isLoadingBlocked: boolean;
  blockedError: string | null;
  
  // Block status cache
  blockStatusCache: Record<number, BlockStatusResponse>;
  
  // Report submission state
  isSubmittingReport: boolean;
  reportError: string | null;
  
  // Block/unblock action state
  isBlockingUser: boolean;
  blockError: string | null;
  
  // Actions
  loadBlockedUsers: () => Promise<void>;
  blockUser: (userId: number, data?: BlockUserRequest) => Promise<void>;
  unblockUser: (userId: number) => Promise<void>;
  getBlockStatus: (userId: number, forceRefresh?: boolean) => Promise<BlockStatusResponse>;
  reportUser: (reportData: ReportSubmission) => Promise<void>;
  clearErrors: () => void;
}

export const useUserManagementStore = create<UserManagementState>((set, get) => ({
  // Initial state
  blockedUsers: [],
  isLoadingBlocked: false,
  blockedError: null,
  blockStatusCache: {},
  isSubmittingReport: false,
  reportError: null,
  isBlockingUser: false,
  blockError: null,

  // Load blocked users
  loadBlockedUsers: async () => {
    set({ isLoadingBlocked: true, blockedError: null });
    
    try {
      const blockedUsers = await userManagementService.getBlockedUsers();
      set({ blockedUsers, isLoadingBlocked: false });
    } catch (error: any) {
      console.error('Failed to load blocked users:', error);
      set({ 
        blockedError: error.response?.data?.message || 'Failed to load blocked users',
        isLoadingBlocked: false 
      });
    }
  },

  // Block a user
  blockUser: async (userId: number, data: BlockUserRequest = {}) => {
    set({ isBlockingUser: true, blockError: null });
    
    try {
      await userManagementService.blockUser(userId, data);
      
      // Update block status cache
      const { blockStatusCache } = get();
      set({ 
        blockStatusCache: {
          ...blockStatusCache,
          [userId]: {
            isBlocked: true,
            currentUserBlocked: true,
            otherUserBlocked: false,
            canMessage: false
          }
        },
        isBlockingUser: false
      });
      
      // Refresh blocked users list
      get().loadBlockedUsers();
      
    } catch (error: any) {
      console.error('Failed to block user:', error);
      set({ 
        blockError: error.response?.data?.message || 'Failed to block user',
        isBlockingUser: false 
      });
      throw error;
    }
  },

  // Unblock a user
  unblockUser: async (userId: number) => {
    set({ isBlockingUser: true, blockError: null });
    
    try {
      await userManagementService.unblockUser(userId);
      
      // Update block status cache
      const { blockStatusCache } = get();
      set({ 
        blockStatusCache: {
          ...blockStatusCache,
          [userId]: {
            isBlocked: false,
            currentUserBlocked: false,
            otherUserBlocked: false,
            canMessage: true
          }
        },
        isBlockingUser: false
      });
      
      // Refresh blocked users list
      get().loadBlockedUsers();
      
    } catch (error: any) {
      console.error('Failed to unblock user:', error);
      set({ 
        blockError: error.response?.data?.message || 'Failed to unblock user',
        isBlockingUser: false 
      });
      throw error;
    }
  },

  // Get block status with caching
  getBlockStatus: async (userId: number, forceRefresh: boolean = false) => {
    const { blockStatusCache } = get();
    
    // Return cached result if available and not forcing refresh
    if (!forceRefresh && blockStatusCache[userId]) {
      return blockStatusCache[userId];
    }
    
    try {
      const blockStatus = await userManagementService.getBlockStatus(userId);
      
      // Update cache
      set({ 
        blockStatusCache: {
          ...blockStatusCache,
          [userId]: blockStatus
        }
      });
      
      return blockStatus;
    } catch (error: any) {
      console.error('Failed to get block status:', error);
      
      // Return default status if API fails
      const defaultStatus: BlockStatusResponse = {
        isBlocked: false,
        currentUserBlocked: false,
        otherUserBlocked: false,
        canMessage: true
      };
      
      return defaultStatus;
    }
  },

  // Submit a report
  reportUser: async (reportData: ReportSubmission) => {
    set({ isSubmittingReport: true, reportError: null });
    
    try {
      await userManagementService.reportUser(reportData);
      set({ isSubmittingReport: false });
    } catch (error: any) {
      console.error('Failed to submit report:', error);
      set({ 
        reportError: error.response?.data?.message || 'Failed to submit report',
        isSubmittingReport: false 
      });
      throw error;
    }
  },

  // Clear all errors
  clearErrors: () => {
    set({ 
      blockedError: null,
      reportError: null,
      blockError: null
    });
  }
}));